/**
 * The ONE place the V1 dashboard turns AI PPE degrees into krpano hotspot angles
 * (V2CORE-2901). Change the convention here only.
 *
 * krpano hotspot angles: `ath` horizontal -180..180 (0 = image centre),
 * `atv` vertical -90..90 (0 = horizon, positive = down).
 *
 * AI degrees (`location_x` / `location_y` on AI observations):
 * - VIEWER_ANGLES (default, confirmed by the AI team in V2CORE-3431): x =
 *   horizontal angle measured from the image's LEFT edge, wrapped into
 *   -180..180 (0 = left edge, +-180 = image centre); y = vertical angle
 *   90..270, 180 = horizon, 270 = up. Same space as the V2 viewer.
 *   krpano: ath = x - 180 (folded into -180..180), atv = 180 - y.
 * - EQUIRECT_TOP_LEFT (alternative): x 0..360 from the left edge, y 0..180 from
 *   the top. krpano: ath = x - 180, atv = y - 90.
 *
 * Hand-made V1 observations already store krpano angles and are never converted.
 */

export type AiAngleConvention = 'VIEWER_ANGLES' | 'EQUIRECT_TOP_LEFT';

/** Active convention. */
export const AI_ANGLE_CONVENTION: AiAngleConvention = 'VIEWER_ANGLES';

export interface KrpanoAngles {
  ath: number;
  atv: number;
}

/** Converts AI degrees to krpano hotspot angles. */
export function aiDegreesToKrpano(
  x: number | string,
  y: number | string,
  convention: AiAngleConvention = AI_ANGLE_CONVENTION,
): KrpanoAngles {
  const ax = Number(x);
  const ay = Number(y);
  if (convention === 'EQUIRECT_TOP_LEFT') {
    return { ath: clean(ax - 180), atv: clean(ay - 90) };
  }
  return { ath: clean(foldHorizontal(ax - 180)), atv: clean(180 - ay) };
}

export interface PinSource {
  is_ai_generated?: unknown;
  location_x?: number | string | null;
  location_y?: number | string | null;
}

/** Angles to put on a hotspot: numbers for converted AI rows, the stored values otherwise. */
export interface PinAngles {
  ath: number | string | null | undefined;
  atv: number | string | null | undefined;
}

/**
 * Pin angles for any observation: AI rows are converted, every other row is
 * returned exactly as stored. AI rows with missing / non-numeric degrees are
 * also returned as stored so the caller keeps today's behaviour.
 */
export function observationPinAngles(
  observation: PinSource,
  convention: AiAngleConvention = AI_ANGLE_CONVENTION,
): PinAngles {
  const raw = { ath: observation?.location_x, atv: observation?.location_y };
  if (!isAiRow(observation)) {
    return raw;
  }
  if (!isNumeric(observation.location_x) || !isNumeric(observation.location_y)) {
    return raw;
  }
  return aiDegreesToKrpano(
    observation.location_x as number | string,
    observation.location_y as number | string,
    convention,
  );
}

function isAiRow(observation: PinSource | null | undefined): boolean {
  const flag = observation?.is_ai_generated;
  return flag === true || flag === 1 || flag === '1' || flag === 'true';
}

function isNumeric(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value));
}

/** Folds a horizontal angle into -180..180, keeping +180 (not -180) for the seam. */
function foldHorizontal(angle: number): number {
  const folded = ((angle % 360) + 360) % 360;
  return folded > 180 ? folded - 360 : folded;
}

/** Avoids -0 in results (180 - 180 etc.). */
function clean(value: number): number {
  return value === 0 ? 0 : value;
}
